import React, { forwardRef, useEffect, useImperativeHandle } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { ControlledSelect } from '../ui/Select/Select';
import { UserData } from '../../services/user';

const userPreferencesSchema = z.object({
  lang: z.enum(['EN', 'DE']).optional(),
  notifications: z
    .object({
      email: z.boolean().optional(),
      sms: z.boolean().optional(),
      dailyUpdates: z.boolean().optional(),
    })
    .optional(),
});

type UserPreferencesFormData = z.infer<typeof userPreferencesSchema>;

export interface UserPreferencesFormRef {
  submit: () => Promise<UserPreferencesFormData | null>;
  getCurrentValues: () => UserPreferencesFormData;
  isValid: () => boolean;
}

type Props = {
  readOnly?: boolean;
  onSubmit?: (data: UserPreferencesFormData) => void;
  initialData?: UserData | null | undefined;
};

export const UserPreferencesForm = forwardRef<UserPreferencesFormRef, Props>(
  ({ readOnly, onSubmit, initialData }, ref) => {
    const { t } = useTranslation();

    const { handleSubmit, register, control, getValues, formState, reset } = useForm<UserPreferencesFormData>({
      resolver: zodResolver(userPreferencesSchema),
      defaultValues: {
        lang: 'EN',
        notifications: {
          email: false,
          sms: false,
          dailyUpdates: false,
        },
      },
    });

    useEffect(() => {
      if (initialData) {
        reset({
          lang: initialData?.lang === 'de_DE' ? 'DE' : 'EN',
          notifications: {
            email: initialData?.notification_email || false,
            sms: initialData?.notification_sms || false,
            dailyUpdates: initialData?.notification_daily_updates || false,
          },
        });
      }
    }, [initialData, reset]);

    useImperativeHandle(ref, () => ({
      submit: () =>
        new Promise<UserPreferencesFormData | null>(resolve => {
          handleSubmit(
            data => resolve(data),
            () => resolve(null),
          )();
        }),
      getCurrentValues: () => getValues(),
      isValid: () => formState.isValid,
    }));

    return (
      <form onSubmit={onSubmit ? handleSubmit(onSubmit) : undefined} className="flex flex-col w-full gap-16">
        <div className="flex flex-col w-full gap-24">
          <span className="typo-text-16--bold">{t('general.language')}</span>
          <div className="flex items-center w-full gap-12">
            <span className="typo-text-14--bold text-faded w-full max-w-[215px]">
              {t('general.language_preference')}
            </span>
            <ControlledSelect
              options={[
                { value: 'EN', label: t('general.lang_english') },
                { value: 'DE', label: t('general.lang_german') },
              ]}
              control={control}
              {...register('lang')}
              placeholder={t('general.language')}
              disabled={readOnly}
              className="w-full"
            />
          </div>
          {/* <Divider className="my-24" /> */}

          {/* <div className="flex flex-col md:flex-row w-full gap-[2rem] md:gap-[8rem] items-start"> */}
          {/*  <span className="typo-text-16--bold">{t('navbar.notifications')}</span> */}
          {/*  <div className="flex flex-col w-full gap-16"> */}
          {/*    {notificationTypes.map(notification => ( */}
          {/*      <div key={notification.key} className="flex items-start gap-12"> */}
          {/*        <ControlledToggle control={control} name={`notifications.${notification.key}`} disabled={readOnly} /> */}
          {/*        <div className="flex flex-col gap-2"> */}
          {/*          <span className="typo-text-14--bold">{notification.label}</span> */}
          {/*        </div> */}
          {/*      </div> */}
          {/*    ))} */}
          {/*  </div> */}
          {/* </div> */}
        </div>
      </form>
    );
  },
);
